import { Checkbox, Form, Input, Select } from "antd";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import AppSelect from "../../../AppSelect";
import styled from "styled-components";
import FieldSelect from "../../../FieldSelect";
import { FormInstance } from "antd/es/form/Form";
import { useFetchRecord } from "@lhd/hooks";
import { CustomAppSelectOption } from "@lhd/features/Apps/AppForm/constants";
import { getListValuesOfField, getOrgsData } from "@lhd/features/Apps/utils";
import { cloneDeep } from "lodash";
import { OrgNode } from "types/orgNode.type";
import { useFetchPositions } from "@lhd/hooks";
import { Position } from "types/position.type";
import { ERRORS_MESSAGE } from "@lhd/constants";

const RelatedRecordsSetting = ({
  form,
  getFieldClassName,
}: {
  form: FormInstance<any>;
  getFieldClassName?: (name: string) => string;
}) => {
  const appId = Form.useWatch("referenced_type", form);
  const appPartId = Form.useWatch("referenced_app_part_id", form);
  const workerProperty = Form.useWatch("referenced_worker_property", form);
  const { data } = useFetchRecord(appId || "");
  const [options, setOptions] = useState<{ label: string; value: string }[]>();
  const { data: positionData } = useFetchPositions(1, 1000, {});
  const listRecordValue = useMemo(() => {
    return data?.data
      ?.map((recordData) =>
        recordData.app_record_values.find(
          (value) => value.app_part_id.toString() === appPartId?.toString(),
        ),
      )
      .filter((v) => v);
  }, [appPartId, data?.data]);
  const getChild = (item: OrgNode, rs: OrgNode[]) => {
    rs.push(item);
    if (item.children) {
      (item.children as unknown as OrgNode[]).forEach((i) => getChild(i, rs));
    }
  };
  const getOptions = useCallback(async () => {
    if (appId === CustomAppSelectOption.ORG) {
      const orgNodeList = await getOrgsData(appPartId);
      const listWithChild: OrgNode[] = [];
      (orgNodeList || []).forEach((item: OrgNode) =>
        getChild(item, listWithChild),
      );
      const optionByOrg = listWithChild?.map((orgItem: OrgNode) => {
        return {
          label: orgItem?.name,
          value: orgItem?.name,
        };
      });
      setOptions(optionByOrg);
      const formOptionByOrg = listWithChild?.map((orgItem: OrgNode) => {
        return {
          text: orgItem?.name?.toString(),
          value: orgItem?.id?.toString(),
        };
      });
      form.setFieldValue(["item_config_data", "options"], formOptionByOrg);
    } else if (appId === CustomAppSelectOption.POSITION) {
      const optionByPosition = positionData?.data?.map(
        (positionItem: Position) => {
          return {
            label: appPartId === 0 ? positionItem?.name : positionItem?.id,
            value: appPartId === 0 ? positionItem?.name : positionItem?.id,
          };
        },
      );
      setOptions(optionByPosition);
      const formOptionByOrg = positionData?.data?.map(
        (positionItem: Position) => {
          return {
            text:
              appPartId === 0
                ? positionItem?.name?.toString()
                : positionItem?.id?.toString(),
            value:
              appPartId === 0
                ? positionItem?.name?.toString()
                : positionItem?.id?.toString(),
          };
        },
      );
      form.setFieldValue(["item_config_data", "options"], formOptionByOrg);
    } else if (appId === CustomAppSelectOption.WORKER) {
      // const listTextField: string[] = await getListWorkerTextField();
      const listValuesOfField: string[] = await getListValuesOfField(
        workerProperty,
      );
      const optionByWorker = listValuesOfField.map((value) => {
        return {
          label: value,
          value: value,
        };
      });
      setOptions(optionByWorker);
      const formOptionByOrg = listValuesOfField?.map((orgItem) => {
        return {
          text: orgItem,
          value: orgItem,
        };
      });
      form.setFieldValue(["item_config_data", "options"], formOptionByOrg);
    } else {
      if (!listRecordValue?.length) return [];
      const optionsBylistRecordValue = listRecordValue?.length
        ? cloneDeep(listRecordValue)
            ?.filter((v) => v?.value)
            ?.map((v) => {
              return {
                label: v?.value || "",
                value: v?.value || "",
              };
            })
        : [];
      setOptions(optionsBylistRecordValue);
      const formOptionByAppPart = optionsBylistRecordValue?.map((item) => {
        return {
          text: item.label,
          value: item.label,
        };
      });
      form.setFieldValue(["item_config_data", "options"], formOptionByAppPart);
    }
  }, [
    appId,
    appPartId,
    listRecordValue,
    positionData?.data,
    workerProperty,
    form,
  ]);
  useEffect(() => {
    getOptions();
  }, [getOptions]);
  return (
    <>
      <Form.Item
        label="列名"
        name="name"
        rules={[{ required: true, message: ERRORS_MESSAGE.REQUIRED }]}
        className={`${getFieldClassName?.("name")}`}
      >
        <Input />
      </Form.Item>
      <Form.Item name={["item_config_data", "options"]} hidden></Form.Item>
      <Form.Item
        valuePropName="checked"
        name="is_hide_name"
        style={{ marginBottom: "0.5rem" }}
      >
        <Checkbox>フィールド名を表示しない</Checkbox>
      </Form.Item>
      <Form.Item
        valuePropName="checked"
        name="is_required_field"
        style={{ marginBottom: "0.5rem" }}
      >
        <Checkbox>必須項目にする</Checkbox>
      </Form.Item>
      <Space>
        <AppSelect form={form} getFieldClassName={getFieldClassName} />
        <FieldSelect
          appId={appId}
          form={form}
          getFieldClassName={getFieldClassName}
        />
      </Space>
      <Form.Item name="default_value" label="デフォルト値">
        <Select showSearch options={options || []} placeholder="デフォルト値" />
      </Form.Item>
      <Form.Item
        label="列コード"
        name="field_code"
        rules={[{ required: true, message: ERRORS_MESSAGE.REQUIRED }]}
        className={`${getFieldClassName?.("field_code")}`}
      >
        <Input />
      </Form.Item>
    </>
  );
};

const Space = styled.div`
  display: flex;
  justify-content: space-between;
`;

export default RelatedRecordsSetting;
